import OutlinedButton from "~/components/common/outlined-button/OutlinedButton";
import styles from "./CreateInvoice.module.scss";
import classNames from "classnames/bind";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import PrimarySelectInput from "~/components/common/primary-select-input/PrimarySelectInput";
import PrimaryInput from "~/components/common/primary-input/PrimaryInput";
import { ChevronsUpIcon, PencilIcon, PlusIcon } from "lucide-react";
import {
  ActionIcon,
  ArrowLeft2Icon,
  DragIcon,
  WarningIcon,
} from "~/assets/icons";
import { useEffect, useState, useRef } from "react";
import crmApi, { ICustomer } from "~/apis/crm";
import { useAppDispatch, useAppSelector } from "~/redux/hooks";
import { handleCreateInvoice } from "~/thunks/job-details/jobDetailsThunk";
import { notify, handleApiError } from "~/utils/helper";
import SectionTable from "./section-table/SectionTable";
import {
  IInvoiceJobDetails,
  InvoiceFormSource,
  IOutputProduct,
} from "~/utils/interface/jobDetails";
import jobDetailsApi from "~/apis/jobDetails";
import LoadingMainContent from "~/components/common/loading-main-content/LoadingMainContent";

const cx = classNames.bind(styles);

interface LineItem {
  id: string;
  itemNumber?: string;
  itemName: string;
  description: string;
  quantity: number;
  uom: string;
  type: "Material" | "Labor";
  markup: number;
  price: number;
}

interface Section {
  id: string;
  sectionTitle: string;
  sectionDescription: string;
  lines: LineItem[];
  hideSectionTotals?: boolean;
}

interface FormData {
  customerId: string;
  selectDate: string;
  designTemplate: string;
  terms: string;
  dueDate: string;
  customerNotes: string;
  internalNotes: string;
  status: string;
  taxRate: string;
  secondaryBillingAddress: string;
}

interface FormErrors {
  [key: string]: string;
}

interface SectionType {
  id: string;
  value: string;
}

const DESIGN_TEMPLATES_OPTIONS = [
  { label: "Invoice", value: "Invoice" },
  { label: "Invoice - Insurance", value: "Invoice - Insurance" },
  { label: "Invoice - Commercial", value: "Invoice - Commercial" },
  { label: "Invoice - PMR", value: "Invoice - PMR" },
];

// Tax rate option value is "<rate>|<countyId>": unique per county (several counties
// share a rate) while parseFloat(value) still yields the rate. "none" parses to NaN -> 0.
const NO_TAX_OPTION = { label: "None", value: "none" };

const TERMS_OPTIONS = [
  { label: "Due On Receipt", value: "Due On Receipt" },
  { label: "Net 15", value: "Net 15" },
  { label: "Net 30", value: "Net 30" },
  { label: "Net 45", value: "Net 45" },
  { label: "Net 60", value: "Net 60" },
  { label: "Custom", value: "Custom" },
  { label: "Split", value: "Split" },
];

interface CreateInvoiceProps {
  jobId: string;
  onSaveInvoice: (invoiceId?: string) => void;
  onCancel: () => void;
  listInvoice: IInvoiceJobDetails[];
  // FB-25: open the form pre-filled from an existing invoice (edit = PUT it, duplicate = POST a copy)
  source?: InvoiceFormSource;
}

const CreateInvoice = ({
  jobId,
  onSaveInvoice,
  onCancel,
  listInvoice,
  source,
}: CreateInvoiceProps) => {
  const isEdit = source?.mode === "edit";
  const dispatch = useAppDispatch();
  const { jobDetails } = useAppSelector((state) => state.jobDetails);
  // FB-22: default the invoice contact to the contact of the job being viewed.
  const jobContactId = String(
    jobDetails?.relatedContacts?.primaryContact?.id ??
      jobDetails?.relatedContacts?.secondaryContact?.id ??
      ""
  );

  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  // Helper function to calculate due date (days from a given date)
  const calculateDueDate = (fromDate: string, daysToAdd: number = 30) => {
    if (!fromDate) return "";
    const date = new Date(fromDate);
    date.setDate(date.getDate() + daysToAdd);
    return date.toISOString().split("T")[0];
  };

  const [formData, setFormData] = useState<FormData>({
    customerId: jobContactId,
    selectDate: getTodayDate(),
    designTemplate: "",
    terms: "Net 30",
    dueDate: calculateDueDate(getTodayDate()),
    customerNotes: "",
    internalNotes: "",
    status: "",
    taxRate: "none",
    secondaryBillingAddress: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (jobContactId) {
      setFormData((prev) => (prev.customerId ? prev : { ...prev, customerId: jobContactId }));
    }
  }, [jobContactId]);
  const [savingAction, setSavingAction] = useState<"draft" | "invoice" | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [contactData, setContactData] = useState<ICustomer[]>([]);
  const [taxRateOptions, setTaxRateOptions] = useState([NO_TAX_OPTION]);
  const [sectionType, setSectionType] = useState<SectionType[]>([
    {
      id: "1",
      value: "SHOW_LINE_ITEMS",
    },
  ]);

  const [sections, setSections] = useState<Section[]>([
    {
      id: "1",
      sectionTitle: "Section 1",
      sectionDescription: "",
      lines: [
        {
          id: "1",
          itemName: "",
          description: "",
          quantity: 0,
          uom: "SQ",
          type: "Material",
          markup: 0,
          price: 0,
        },
      ],
      hideSectionTotals: false,
    },
  ]);

  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [tempSectionTitle, setTempSectionTitle] = useState<string>("");
  const [sectionTitleErrors, setSectionTitleErrors] = useState<{
    [key: string]: string;
  }>({});
  const sectionTitleInputRefs = useRef<{
    [key: string]: HTMLDivElement | null;
  }>({});
  const sectionTitleInputElementRefs = useRef<{
    [key: string]: HTMLInputElement | null;
  }>({});
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [collapsedSections, setCollapsedSections] = useState<{
    [key: string]: boolean;
  }>({});
  const [isAllCollapsed, setIsAllCollapsed] = useState(false);
  const [sectionTableData, setSectionTableData] = useState<{
    [sectionId: string]: { formData: LineItem[]; sectionTotal: number };
  }>({});
  // ABC Supply catalog only feeds item suggestions; if it is down, items can still be typed in.
  const [productCatalogError, setProductCatalogError] = useState("");
  const [listProductInvoice, setListProductInvoice] = useState<
    IOutputProduct[]
  >([]);
  const [selectedSavedInvoice, setSelectedSavedInvoice] =
    useState<string>("none");
  const [showSummaryDropdown, setShowSummaryDropdown] = useState(false);
  const [summaryDropdownPosition, setSummaryDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const summaryButtonRef = useRef<HTMLDivElement | null>(null);
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);

  // Auto-calculate due date when selectDate or terms change
  useEffect(() => {
    if (formData.selectDate && formData.terms) {
      let daysToAdd = 30; // Default to 30 days

      // Parse the term to get the number of days
      if (formData.terms === "Due On Receipt") {
        daysToAdd = 0;
      } else if (formData.terms.startsWith("Net ")) {
        const days = parseInt(formData.terms.split(" ")[1]);
        if (!isNaN(days)) {
          daysToAdd = days;
        }
      }
      // For "Custom" and "Split", keep default 30 days

      const newDueDate = calculateDueDate(formData.selectDate, daysToAdd);
      if (newDueDate !== formData.dueDate) {
        setFormData((prev) => ({ ...prev, dueDate: newDueDate }));
      }
    }
  }, [formData.selectDate, formData.terms]);

  useEffect(() => {
    const fetchContactData = async () => {
      try {
        const response = await crmApi.getCustomersList({});
        setContactData(response.data?.data?.responses);
      } catch (error) {
        handleApiError(error);
      }
    };

    const fetchListProductInvoice = async () => {
      try {
        const response = await jobDetailsApi.getListProductInvoice(jobId, {
          description: "S",
          state: "UT",
        });
        setListProductInvoice(response.data?.data?.outputProduct);
        setProductCatalogError("");
      } catch (error: any) {
        // Non-blocking: no error toast when opening Create Invoice (bug #33 retest)
        setProductCatalogError(
          `ABC Supply product suggestions are unavailable right now (${error?.message || "service error"}). You can still enter items manually.`
        );
      }
    };

    const fetchTaxRates = async () => {
      try {
        const response = await jobDetailsApi.getInvoiceTaxRates(jobId);
        const counties = response.data?.data ?? [];
        setTaxRateOptions([
          NO_TAX_OPTION,
          ...counties.map((c) => ({
            label: `${c.county}, ${c.state} (${c.salesTax}%)`,
            value: `${c.salesTax}|${c.id}`,
          })),
        ]);
      } catch (error) {
        handleApiError(error);
      }
    };

    setIsLoading(true);
    Promise.all([fetchListProductInvoice(), fetchContactData(), fetchTaxRates()])
      .then(() => {
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [jobId]);

  // Handle click outside to save section title
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editingSectionId) {
        const ref = sectionTitleInputRefs.current[editingSectionId];
        if (ref && !ref.contains(event.target as Node)) {
          // Inline save logic to avoid dependency issues
          const trimmedTitle = tempSectionTitle.trim();

          // Validate
          if (!trimmedTitle) {
            setSectionTitleErrors((prev) => ({
              ...prev,
              [editingSectionId]: "Section title is required",
            }));
            return;
          }

          // Save the title
          setSections((prevSections) =>
            prevSections.map((section) =>
              section.id === editingSectionId
                ? { ...section, sectionTitle: trimmedTitle }
                : section
            )
          );
          setEditingSectionId(null);
          setTempSectionTitle("");
          setSectionTitleErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[editingSectionId];
            return newErrors;
          });
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [editingSectionId, tempSectionTitle]);

  const handleSectionTypeChange = (sectionId: string, value: string) => {
    setSectionType((prev) => {
      const existingIndex = prev.findIndex((st) => st.id === sectionId);
      let updated = [...prev];
      if (existingIndex >= 0) {
        updated[existingIndex] = { id: sectionId, value };
      } else {
        updated.push({ id: sectionId, value });
      }
      return updated;
    });
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSavedInvoiceChange = async (value: string, mode?: InvoiceFormSource["mode"]) => {
    setSelectedSavedInvoice(value);

    if (value === "none") {
      // Clear form data with automatic defaults
      const todayDate = getTodayDate();
      setFormData({
        customerId: jobContactId,
        selectDate: todayDate,
        designTemplate: "",
        terms: "Net 30",
        dueDate: calculateDueDate(todayDate, 30),
        customerNotes: "",
        internalNotes: "",
        status: "",
        taxRate: "none",
        secondaryBillingAddress: "",
      });
      setSections([
        {
          id: "1",
          sectionTitle: "Section 1",
          sectionDescription: "",
          lines: [
            {
              id: "1",
              itemName: "",
              description: "",
              quantity: 0,
              uom: "SQ",
              type: "Material",
              markup: 0,
              price: 0,
            },
          ],
          hideSectionTotals: false,
        },
      ]);
      setSectionType([
        {
          id: "1",
          value: "SHOW_LINE_ITEMS",
        },
      ]);
      setSectionTableData({});
      return;
    }

    // Call API to get invoice details
    try {
      setIsLoading(true);
      const response = await jobDetailsApi.getInvoiceDetailsJobDetails(
        jobId,
        value
      );
      const invoiceData = response.data?.data;

      if (invoiceData) {
        const savedTaxRate = toTaxRateValue(Number(invoiceData.taxRate)); // may add an option -> keep out of the updater
        // Map form data
        setFormData((prev) => ({
          ...prev,
          customerId: invoiceData.related?.contactId || "",
          selectDate:
            mode === "duplicate"
              ? getTodayDate()
              : invoiceData.invoiceDate
              ? new Date(invoiceData.invoiceDate).toISOString().split("T")[0]
              : "",
          dueDate: invoiceData.dueDate
            ? new Date(invoiceData.dueDate).toISOString().split("T")[0]
            : "",
          customerNotes: invoiceData.customerNotes || "",
          internalNotes: mode === "edit" ? invoiceData.internalNotes || "" : "",
          status: invoiceData.status || "",
          designTemplate: invoiceData.designTemplate || "",
          terms: invoiceData.terms || "",
          secondaryBillingAddress: invoiceData.secondaryBillingAddress || "",
          taxRate: savedTaxRate,
        }));

        // Map sections
        if (invoiceData.sections && invoiceData.sections.length > 0) {
          const baseTimestamp = Date.now();
          const mappedSections: Section[] = invoiceData.sections.map(
            (section, sectionIndex) => ({
              id: `${baseTimestamp}-${sectionIndex}`,
              sectionTitle:
                section.sectionTitle || `Section ${sectionIndex + 1}`,
              sectionDescription: section.sectionDescription || "",
              lines: section.lines.map((line, lineIndex) => ({
                id: `${baseTimestamp}-${sectionIndex}-${lineIndex}`,
                itemNumber: line.itemNumber || "",
                itemName: line.itemName || "",
                description: line.description || "",
                quantity: line.quantity || 0,
                uom: line.uom || "SQ",
                type: (line.type as "Material" | "Labor") || "Material",
                markup: line.markup || 0,
                price: line.price || 0,
              })),
              hideSectionTotals: false,
            })
          );

          const mappedSectionType: SectionType[] = mappedSections.map(
            (section) => ({
              id: section.id,
              value: "SHOW_LINE_ITEMS",
            })
          );

          setSections(mappedSections);
          setSectionType(mappedSectionType);
          setSectionTableData({});
        } else {
          // If no sections, reset to default
          setSections([
            {
              id: "1",
              sectionTitle: "Section 1",
              sectionDescription: "",
              lines: [
                {
                  id: "1",
                  itemName: "",
                  description: "",
                  quantity: 0,
                  uom: "SQ",
                  type: "Material",
                  markup: 0,
                  price: 0,
                },
              ],
              hideSectionTotals: false,
            },
          ]);
          setSectionType([
            {
              id: "1",
              value: "SHOW_LINE_ITEMS",
            },
          ]);
          setSectionTableData({});
        }
      }
      setIsLoading(false);
    } catch (error) {
      handleApiError(error);
      setIsLoading(false);
    }
  };

  const handleAddSection = () => {
    const newSection: Section = {
      id: Date.now().toString(),
      sectionTitle: `Section ${sections.length + 1}`,
      sectionDescription: "",
      lines: [
        {
          id: Date.now().toString(),
          itemName: "",
          description: "",
          quantity: 0,
          uom: "SQ",
          type: "Material",
          markup: 0,
          price: 0,
        },
      ],
      hideSectionTotals: false,
    };

    const newSectionType: SectionType = {
      id: Date.now().toString(),
      value: "SHOW_LINE_ITEMS",
    };
    setSections([...sections, newSection]);
    setSectionType([...sectionType, newSectionType]);
  };

  const handleDeleteSection = (sectionId: string) => {
    if (sections.length > 1) {
      setSections(sections.filter((section) => section.id !== sectionId));
      // Cleanup sectionTableData when section is deleted
      setSectionTableData((prev) => {
        const newData = { ...prev };
        delete newData[sectionId];
        return newData;
      });
      // Cleanup sectionType when section is deleted
      setSectionType((prev) => prev.filter((st) => st.id !== sectionId));
    }
  };

  const handleSectionChange = (
    sectionId: string,
    field: keyof Section,
    value: string
  ) => {
    setSections(
      sections.map((section) =>
        section.id === sectionId ? { ...section, [field]: value } : section
      )
    );
  };

  const handleStartEditSectionTitle = (sectionId: string) => {
    const section = sections.find((s) => s.id === sectionId);
    if (section) {
      setEditingSectionId(sectionId);
      setTempSectionTitle(section.sectionTitle);
      setSectionTitleErrors((prev) => ({ ...prev, [sectionId]: "" }));
    }
  };

  // Auto-focus input when editing starts
  useEffect(() => {
    if (editingSectionId) {
      const inputElement =
        sectionTitleInputElementRefs.current[editingSectionId];
      if (inputElement) {
        setTimeout(() => {
          inputElement.focus();
          inputElement.select();
        }, 0);
      }
    }
  }, [editingSectionId]);

  // Action Menu Component
  const ActionMenu = ({
    section,
    onClose,
    position,
  }: {
    section: Section;
    onClose: () => void;
    position: { top: number; left: number };
  }) => {
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          menuRef.current &&
          !menuRef.current.contains(event.target as Node)
        ) {
          onClose();
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    const handleToggleHideSectionTotals = () => {
      setSections((prevSections) =>
        prevSections.map((s) =>
          s.id === section.id
            ? { ...s, hideSectionTotals: !s.hideSectionTotals }
            : s
        )
      );
    };

    const handleDelete = () => {
      handleDeleteSection(section.id);
      onClose();
    };

    return (
      <div
        ref={menuRef}
        className={cx("action-menu")}
        style={{
          top: position.top,
          left: position.left,
        }}
      >
        <div className={cx("menu-item", "menu-item-toggle")}>
          <span>Hide section totals</span>
          <div
            className={cx("toggle-switch", {
              active: section.hideSectionTotals,
            })}
            onClick={(e) => {
              e.stopPropagation();
              handleToggleHideSectionTotals();
            }}
          >
            <div className={cx("toggle-circle")} />
          </div>
        </div>
        <button className={cx("menu-item")} onClick={handleDelete}>
          Delete Section
        </button>
      </div>
    );
  };

  const handleActionClick = (event: React.MouseEvent, section: Section) => {
    event.stopPropagation();

    if (activeMenu === section.id) {
      setActiveMenu(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + 5,
      left: rect.left - 100,
    });
    setActiveMenu(section.id);
  };

  const handleSectionTableDataChange = (
    sectionId: string,
    data: { formData: LineItem[]; sectionTotal: number }
  ) => {
    setSectionTableData((prev) => ({
      ...prev,
      [sectionId]: data,
    }));
  };

  const calculateLineTotal = (line: LineItem) => {
    const qty = parseFloat(line.quantity.toString()) || 0;
    const price = parseFloat(line.price.toString()) || 0;
    const markup = parseFloat(line.markup?.toString() || "0") || 0;
    const subtotal = qty * price;
    const total = subtotal + (subtotal * markup) / 100;
    return total;
  };

  const calculateSectionTotal = (section: Section) => {
    const tableData = sectionTableData[section.id];
    if (tableData) {
      return tableData.sectionTotal;
    }
    return section.lines.reduce((total, line) => {
      return total + calculateLineTotal(line);
    }, 0);
  };

  const calculateInvoiceTotal = () => {
    return sections.reduce((total, section) => {
      return total + calculateSectionTotal(section);
    }, 0);
  };

  const calculateTotalProfit = () => {
    return sections.reduce((totalProfit, section) => {
      const tableData = sectionTableData[section.id];
      if (tableData) {
        const profit = tableData.formData.reduce((sectionProfit, line) => {
          const qty = parseFloat(line.quantity.toString()) || 0;
          const price = parseFloat(line.price.toString()) || 0;
          const markup = parseFloat(line.markup?.toString() || "0") || 0;
          return sectionProfit + (qty * price * markup) / 100;
        }, 0);
        return totalProfit + profit;
      }

      return (
        totalProfit +
        section.lines.reduce((sectionProfit, line) => {
          const qty = parseFloat(line.quantity.toString()) || 0;
          const price = parseFloat(line.price.toString()) || 0;
          const markup = parseFloat(line.markup?.toString() || "0") || 0;
          const profit = (qty * price * markup) / 100;
          return sectionProfit + profit;
        }, 0)
      );
    }, 0);
  };

  const calculateTotalMargin = () => {
    const costWithMarkup = sections.reduce((total, section) => {
      const tableData = sectionTableData[section.id];
      const lines = tableData ? tableData.formData : section.lines;

      return (
        total +
        lines.reduce((sectionTotal, line) => {
          const qty = parseFloat(line.quantity.toString()) || 0;
          const price = parseFloat(line.price.toString()) || 0;
          const markup = parseFloat(line.markup?.toString() || "0") || 0;

          if (markup > 0) {
            return sectionTotal + qty * price;
          }
          return sectionTotal;
        }, 0)
      );
    }, 0);

    const profit = calculateTotalProfit();
    if (costWithMarkup === 0) return 0;
    return (profit / costWithMarkup) * 100;
  };

  const calculateMaterialCost = () => {
    return sections.reduce((total, section) => {
      const tableData = sectionTableData[section.id];
      const lines = tableData ? tableData.formData : section.lines;

      return (
        total +
        lines.reduce((sectionTotal, line) => {
          if (line.type === "Material") {
            const qty = parseFloat(line.quantity.toString()) || 0;
            const price = parseFloat(line.price.toString()) || 0;
            return sectionTotal + qty * price;
          }
          return sectionTotal;
        }, 0)
      );
    }, 0);
  };

  const calculateLaborCost = () => {
    return sections.reduce((total, section) => {
      const tableData = sectionTableData[section.id];
      const lines = tableData ? tableData.formData : section.lines;

      return (
        total +
        lines.reduce((sectionTotal, line) => {
          if (line.type === "Labor") {
            const qty = parseFloat(line.quantity.toString()) || 0;
            const price = parseFloat(line.price.toString()) || 0;
            return sectionTotal + qty * price;
          }
          return sectionTotal;
        }, 0)
      );
    }, 0);
  };

  const calculateCost = () => {
    return calculateMaterialCost() + calculateLaborCost();
  };

  const calculateMarkupAmount = () => {
    return calculateTotalProfit();
  };

  const calculateMarkupPercentage = () => {
    const costWithMarkup = sections.reduce((total, section) => {
      const tableData = sectionTableData[section.id];
      const lines = tableData ? tableData.formData : section.lines;

      return (
        total +
        lines.reduce((sectionTotal, line) => {
          const qty = parseFloat(line.quantity.toString()) || 0;
          const price = parseFloat(line.price.toString()) || 0;
          const markup = parseFloat(line.markup?.toString() || "0") || 0;

          if (markup > 0) {
            return sectionTotal + qty * price;
          }
          return sectionTotal;
        }, 0)
      );
    }, 0);

    const markup = calculateMarkupAmount();
    if (costWithMarkup === 0) return 0;
    return (markup / costWithMarkup) * 100;
  };

  const calculateSubtotal = () => {
    return calculateCost() + calculateMarkupAmount();
  };

  const toTaxRateValue = (rate: number) => {
    if (!rate) return NO_TAX_OPTION.value;
    const found = taxRateOptions.find((o) => parseFloat(o.value) === rate);
    if (found) return found.value;
    // Rate no longer in the county list (changed/removed): keep it selectable so the saved value shows.
    const saved = { label: `${rate}%`, value: `${rate}|saved` };
    setTaxRateOptions((prev) => (prev.some((o) => o.value === saved.value) ? prev : [...prev, saved]));
    return saved.value;
  };

  const calculateTax = () => {
    const subtotal = calculateSubtotal();
    if (formData.taxRate && formData.taxRate !== "none") {
      const taxRate = parseFloat(formData.taxRate) || 0;
      return (subtotal * taxRate) / 100;
    }
    return 0;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const calculateSummaryDropdownPosition = () => {
    if (summaryButtonRef.current) {
      const rect = summaryButtonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const dropdownWidth = 500;
      const dropdownMaxHeight = 600;
      const spacing = 2;
      const padding = 8;

      let left = rect.left;

      if (left + dropdownWidth > viewportWidth - padding) {
        left = Math.max(padding, viewportWidth - dropdownWidth - padding);
      }

      if (left < padding) {
        left = padding;
      }

      const spaceBelow = viewportHeight - rect.bottom - spacing - padding;
      const spaceAbove = rect.top - spacing - padding;

      let top: number;

      if (spaceBelow < 300 && spaceAbove > spaceBelow) {
        const estimatedHeight = Math.min(dropdownMaxHeight, spaceAbove);
        top = rect.top - estimatedHeight - spacing + 44;

        if (top < padding) {
          top = padding;
        }
      } else {
        top = rect.bottom + spacing + 44;

        const maxAvailableHeight = spaceBelow;
        if (maxAvailableHeight < 200) {
          const estimatedHeight = Math.min(dropdownMaxHeight, spaceAbove);
          top = Math.max(padding, rect.top - estimatedHeight - spacing + 44);
        }
      }

      return {
        top,
        left,
      };
    }
    return null;
  };

  const handleShowSummaryClick = () => {
    setShowSummaryDropdown(true);
    requestAnimationFrame(() => {
      const position = calculateSummaryDropdownPosition();
      if (position) {
        setSummaryDropdownPosition(position);
      }
    });
  };

  useEffect(() => {
    if (!showSummaryDropdown) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const dropdown = document.querySelector("[data-summary-dropdown]");

      if (
        summaryButtonRef.current &&
        !summaryButtonRef.current.contains(target) &&
        (!dropdown || !dropdown.contains(target))
      ) {
        setShowSummaryDropdown(false);
        setSummaryDropdownPosition(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSummaryDropdown]);

  useEffect(() => {
    if (!showSummaryDropdown) return;

    const handleResize = () => {
      const position = calculateSummaryDropdownPosition();
      if (position) {
        setSummaryDropdownPosition(position);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [showSummaryDropdown]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Required fields
    if (!formData.selectDate) newErrors.selectDate = "Date is required";
    if (!formData.designTemplate)
      newErrors.designTemplate = "Design template is required";
    if (!formData.terms) newErrors.terms = "Terms is required";
    if (!formData.dueDate) newErrors.dueDate = "Due date is required";

    // Validate dates
    if (formData.selectDate && formData.dueDate) {
      const selectDate = new Date(formData.selectDate);
      const dueDate = new Date(formData.dueDate);
      if (dueDate < selectDate) {
        newErrors.dueDate = "Due date must be after select date";
      }
    }

    let hasIncompleteLine = false;
    sections.forEach((section) => {
      const tableData = sectionTableData[section.id];
      const lines = tableData ? tableData.formData : section.lines;

      lines.forEach((line) => {
        if (!line.itemName || line.itemName.trim() === "") {
          hasIncompleteLine = true;
        }
      });
    });

    if (hasIncompleteLine) {
      newErrors.sections =
        "Please complete all required fields for line items (Item Name is required)";
    }

    const hasLines = sections.some((section) => {
      const tableData = sectionTableData[section.id];
      const lines = tableData ? tableData.formData : section.lines;
      return lines.length > 0;
    });

    if (sections.length === 0 || !hasLines) {
      newErrors.sections =
        "At least one section with one line item is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const preparePayload = (status?: string) => {
    const payload: any = {
      selectDate: new Date(formData.selectDate).toISOString(),
      designTemplate: formData.designTemplate,
      terms: formData.terms,
      dueDate: new Date(formData.dueDate).toISOString(),
      customerNotes: formData.customerNotes,
      internalNotes: formData.internalNotes,
      secondaryBillingAddress: formData.secondaryBillingAddress,
      taxRate: parseFloat(formData.taxRate) || 0,
      sections: sections.map((section: Section) => {
        const tableData = sectionTableData[section.id];
        const lines = tableData ? tableData.formData : section.lines;

        return {
          sectionTitle: section.sectionTitle,
          sectionDescription: section.sectionDescription,
          lines: lines.map((line) => {
            const lineData: any = {
              itemNumber: line.itemNumber || "",
              itemName: line.itemName,
              description: line.description,
              quantity: parseFloat(line.quantity.toString()),
              uom: line.uom,
              type: line.type,
              price: parseFloat(line.price.toString()),
              markup: parseFloat(line.markup.toString()),
            };

            return lineData;
          }),
        };
      }),
      status: status || formData.status,
    };

    if (formData.customerId) {
      payload.customerId = parseInt(formData.customerId);
    }

    return payload;
  };

  useEffect(() => {
    if (source) handleSavedInvoiceChange(source.invoiceId, source.mode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source?.invoiceId, source?.mode]);

  const handleSaveDraft = async () => {
    if (isSubmitting) return;
    setSavingAction("draft");
    await handleSubmit("Draft");
  };

  const handleSaveInvoice = async () => {
    if (isSubmitting) return;
    setSavingAction("invoice");
    await handleSubmit("Draft");
  };

  const handleSubmit = async (status: string) => {
    if (isSubmitting) {
      return; // Prevent duplicate calls
    }

    if (!status) {
      notify("error", "Status is required");
      return;
    }

    const requiredFieldsFilled =
      formData.selectDate &&
      formData.designTemplate &&
      formData.terms &&
      formData.dueDate;

    let hasIncompleteLine = false;
    let hasNoLines = false;

    sections.forEach((section) => {
      const tableData = sectionTableData[section.id];
      const lines = tableData ? tableData.formData : section.lines;

      if (lines.length === 0) {
        hasNoLines = true;
      }

      lines.forEach((line) => {
        if (!line.itemName || line.itemName.trim() === "") {
          hasIncompleteLine = true;
        }
      });
    });

    const hasLines = sections.some((section) => {
      const tableData = sectionTableData[section.id];
      const lines = tableData ? tableData.formData : section.lines;
      return lines.length > 0;
    });

    if (sections.length === 0 || !hasLines) {
      hasNoLines = true;
    }

    // Validate form first
    if (!validateForm()) {
      if (requiredFieldsFilled && (hasIncompleteLine || hasNoLines)) {
        if (hasIncompleteLine) {
          notify(
            "error",
            "Please complete all line items in sections. Item Name is required for all line items."
          );
        } else if (hasNoLines) {
          notify(
            "error",
            "At least one section with one line item is required. Please add line items to your sections."
          );
        }
      }

      setSavingAction(null);

      // Scroll to first error
      setTimeout(() => {
        const firstErrorField = Object.keys(errors)[0];
        const element = document.getElementsByName(firstErrorField)[0];
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
      return;
    }

    setIsSubmitting(true);
    try {
      // Prepare payload with the provided status
      const payload = preparePayload();
      payload.status = status;

      if (isEdit && source) {
        // Edit keeps the invoice's current status; status changes go through the status menu.
        payload.status = formData.status || status;
        await jobDetailsApi.updateInvoice(jobId, source.invoiceId, payload);
        notify("success", "Invoice updated successfully!");
        onSaveInvoice(source.invoiceId);
        return;
      }

      const res = await dispatch(handleCreateInvoice({ jobId, data: payload })).unwrap();

      notify("success", "Invoice created successfully!");
      onSaveInvoice(res?.data?.id ? String(res.data.id) : undefined);
    } catch (error: any) {
      console.error("Failed to save invoice:", error);
      notify(
        "error",
        error?.response?.data?.message || (isEdit ? "Failed to update invoice" : "Failed to create invoice")
      );
    } finally {
      setIsSubmitting(false);
      setSavingAction(null);
    }
  };

  const handleDragStart = (e: React.DragEvent, section: Section) => {
    setDraggedItemId(section.id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", "");
  };

  const handleDragOver = (e: React.DragEvent, section: Section) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";

    if (draggedItemId && section.id !== draggedItemId) {
      const rect = e.currentTarget.getBoundingClientRect();
      const mouseY = e.clientY;
      const itemMiddle = rect.top + rect.height / 2;

      const currentIndex = sections.findIndex((s) => s.id === draggedItemId);
      const targetIndex = sections.findIndex((s) => s.id === section.id);

      let insertIndex: number;

      if (mouseY < itemMiddle) {
        insertIndex = targetIndex;
      } else {
        insertIndex = targetIndex + 1;
      }

      if (currentIndex < insertIndex) {
        insertIndex = insertIndex - 1;
      }

      if (currentIndex !== insertIndex && insertIndex >= 0) {
        const newItems = [...sections];
        const draggedSection = newItems[currentIndex];
        newItems.splice(currentIndex, 1);
        newItems.splice(insertIndex, 0, draggedSection);
        setSections(newItems);
      }
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDraggedItemId(null);
  };

  const handleDragEnd = () => {
    setDraggedItemId(null);
  };

  return (
    <div className={cx("create-invoice-container")}>
      <div className={cx("header")}>
        <h2 className={cx("title")}>
          {isEdit ? "Edit Invoice" : source?.mode === "duplicate" ? "Duplicate Invoice" : "Create Invoice"}
        </h2>

        <div className={cx("actions")}>
          <OutlinedButton
            title="One Step-by-Step Guide"
            onClick={() => { }}
            className={cx("one-step-by-step-guide-button")}
          />
          <OutlinedButton
            title="Cancel"
            onClick={onCancel}
            disabled={isSubmitting}
          />
          <PrimaryButton
            title="Save as Draft"
            onClick={handleSaveDraft}
            className={cx("save-draft-button")}
            loading={savingAction === "draft"}
            disabled={isSubmitting}
          />

          <PrimaryButton
            title="Save Invoice"
            onClick={handleSaveInvoice}
            className={cx("save-invoice-button")}
            loading={savingAction === "invoice"}
            disabled={isSubmitting}
          />
        </div>
      </div>

      <div className={cx("content")}>
        {isLoading ? (
          <div className={cx("loading-overlay")}>
            <LoadingMainContent />
          </div>
        ) : (
          <>
            <div className={cx("section")}>
              <div className={cx("section-header")}>
                <h3 className={cx("section-title")}>Options</h3>
              </div>

              <div className={cx("section-content")}>
                <div className={cx("options-container")}>
                  <div className={cx("option-item")}>
                    <h4 className={cx("option-item-title")}>Details</h4>

                    <div className={cx("option-item-content")}>
                      <div className={cx("option-item-content-item")}>
                        <label className={cx("option-item-content-item-label")}>
                          Contact
                        </label>

                        <PrimarySelectInput
                          name="customerId"
                          placeholder="Select Contact"
                          value={formData.customerId}
                          onChange={(value) =>
                            handleSelectChange("customerId", value)
                          }
                          options={contactData.map((contact) => ({
                            label: contact.name,
                            value: contact.id,
                          }))}
                          className={cx("select-input", {
                            error: errors.customerId,
                          })}
                          errorMessage={errors.customerId}
                        />
                      </div>

                      <div className={cx("option-item-content-item")}>
                        <label className={cx("option-item-content-item-label")}>
                          Select date<span>*</span>
                        </label>

                        <PrimaryInput
                          name="selectDate"
                          placeholder="Select Date"
                          type="date"
                          value={formData.selectDate}
                          onChange={handleInputChange}
                          className={cx("input", { error: errors.selectDate })}
                          errorMessage={errors.selectDate}
                        />
                      </div>

                      <div className={cx("option-item-content-item")}>
                        <label className={cx("option-item-content-item-label")}>
                          Design Template<span>*</span>
                        </label>

                        <PrimarySelectInput
                          name="designTemplate"
                          placeholder="Select Design Template"
                          value={formData.designTemplate}
                          onChange={(value) =>
                            handleSelectChange("designTemplate", value)
                          }
                          options={DESIGN_TEMPLATES_OPTIONS}
                          className={cx("select-input", {
                            error: errors.designTemplate,
                          })}
                          errorMessage={errors.designTemplate}
                        />
                      </div>

                      {!isEdit && (
                        <div className={cx("option-item-content-item")}>
                          <label className={cx("option-item-content-item-label")}>
                            Saved Invoices
                          </label>

                          <PrimarySelectInput
                            name="saved-invoices"
                            placeholder="Select Saved Invoices"
                            value={selectedSavedInvoice}
                            onChange={(value) => handleSavedInvoiceChange(value)}
                            options={[
                              { label: "None", value: "none" },
                              ...listInvoice.map((invoice) => ({
                                label: invoice.invoiceUniqueCode || "",
                                value: invoice.id,
                              })),
                            ]}
                            className={cx("select-input")}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div
                  className={cx(
                    "options-container",
                    "flex flex-col justify-between"
                  )}
                >
                  <div className={cx("option-item")}>
                    <h4 className={cx("option-item-title")}>Tax Rate</h4>

                    <div className={cx("option-item-content")}>
                      <div className={cx("option-item-content-item")}>
                        <label className={cx("option-item-content-item-label")}>
                          Tax Rate
                        </label>

                        <PrimarySelectInput
                          name="tax-rate"
                          placeholder="Select Tax Rate"
                          value={formData.taxRate}
                          onChange={(value) =>
                            handleSelectChange("taxRate", value)
                          }
                          options={taxRateOptions}
                          className={cx("select-input")}
                        />
                      </div>
                    </div>
                  </div>

                  <div className={cx("option-item")}>
                    <h4 className={cx("option-item-title")}>Payment Terms</h4>

                    <div className={cx("option-item-content")}>
                      <div className={cx("option-item-content-item")}>
                        <label className={cx("option-item-content-item-label")}>
                          Terms<span>*</span>
                        </label>

                        <PrimarySelectInput
                          name="terms"
                          placeholder="Select Terms"
                          value={formData.terms}
                          onChange={(value) =>
                            handleSelectChange("terms", value)
                          }
                          options={TERMS_OPTIONS}
                          className={cx("select-input", {
                            error: errors.terms,
                          })}
                          errorMessage={errors.terms}
                        />
                      </div>

                      <div className={cx("option-item-content-item")}>
                        <label className={cx("option-item-content-item-label")}>
                          Due date<span>*</span>
                        </label>

                        <PrimaryInput
                          name="dueDate"
                          placeholder="Select Due Date"
                          type="date"
                          value={formData.dueDate}
                          onChange={handleInputChange}
                          className={cx("input", { error: errors.dueDate })}
                          errorMessage={errors.dueDate}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={cx("section")}>
              <div className={cx("section-header", "w-full max-w-[773px]")}>
                <h3 className={cx("section-title")}>Sections</h3>

                <OutlinedButton
                  title={isAllCollapsed ? "Expand all" : "Collapse all"}
                  icon={
                    <ChevronsUpIcon
                      className={cx("collapse-icon", {
                        rotated: isAllCollapsed,
                      })}
                    />
                  }
                  onClick={() => {
                    const newCollapsedState: { [key: string]: boolean } = {};
                    sections.forEach((section) => {
                      newCollapsedState[section.id] = !isAllCollapsed;
                    });
                    setCollapsedSections(newCollapsedState);
                    setIsAllCollapsed(!isAllCollapsed);
                  }}
                  className={cx("add-section-button")}
                />
              </div>

              <div className={cx("section-body")}>
                {sections.map((section) => (
                  <div
                    className={cx("section-item", {
                      "is-dragging": draggedItemId === section.id,
                    })}
                    key={section.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, section)}
                    onDragOver={(e) => handleDragOver(e, section)}
                    onDrop={handleDrop}
                    onDragEnd={handleDragEnd}
                  >
                    <button
                      className={cx("drag-button")}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <DragIcon className={cx("drag-icon")} />
                    </button>

                    <div className={cx("section-table-container")}>
                      <div className={cx("section-table-header")}>
                        <div
                          className={cx("section-table-header-left")}
                          ref={(el) => {
                            sectionTitleInputRefs.current[section.id] = el;
                          }}
                        >
                          {editingSectionId === section.id ? (
                            <div className={cx("section-title-input-wrapper")}>
                              <input
                                ref={(el) => {
                                  sectionTitleInputElementRefs.current[
                                    section.id
                                  ] = el;
                                }}
                                name="section-title"
                                placeholder="Section Title"
                                value={tempSectionTitle}
                                onChange={(e) =>
                                  setTempSectionTitle(e.target.value)
                                }
                                onKeyDown={(
                                  e: React.KeyboardEvent<HTMLInputElement>
                                ) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    const trimmedTitle =
                                      tempSectionTitle.trim();
                                    if (!trimmedTitle) {
                                      setSectionTitleErrors((prev) => ({
                                        ...prev,
                                        [section.id]:
                                          "Section title is required",
                                      }));
                                      return;
                                    }
                                    handleSectionChange(
                                      section.id,
                                      "sectionTitle",
                                      trimmedTitle
                                    );
                                    setEditingSectionId(null);
                                    setTempSectionTitle("");
                                    setSectionTitleErrors((prev) => {
                                      const newErrors = { ...prev };
                                      delete newErrors[section.id];
                                      return newErrors;
                                    });
                                  } else if (e.key === "Escape") {
                                    setEditingSectionId(null);
                                    setTempSectionTitle("");
                                    setSectionTitleErrors((prev) => {
                                      const newErrors = { ...prev };
                                      delete newErrors[section.id];
                                      return newErrors;
                                    });
                                  }
                                }}
                                className={cx(
                                  "section-table-header-left-title",
                                  "input",
                                  {
                                    error: sectionTitleErrors[section.id],
                                  }
                                )}
                              />
                              {sectionTitleErrors[section.id] && (
                                <p className={cx("error-message")}>
                                  {sectionTitleErrors[section.id]}
                                </p>
                              )}
                            </div>
                          ) : (
                            <>
                              <p
                                className={cx(
                                  "section-table-header-left-title"
                                )}
                              >
                                {section.sectionTitle}
                              </p>
                              <button
                                className={cx(
                                  "section-table-header-left-button"
                                )}
                                onClick={() =>
                                  handleStartEditSectionTitle(section.id)
                                }
                                type="button"
                              >
                                <PencilIcon className={cx("pencil-icon")} />
                              </button>
                            </>
                          )}
                        </div>

                        <div className={cx("section-table-header-right")}>
                          {!section.hideSectionTotals && (
                            <div
                              className={cx("section-table-header-right-total")}
                            >
                              <label
                                className={cx(
                                  "section-table-header-right-label"
                                )}
                              >
                                Section Total:
                              </label>
                              <p
                                className={cx(
                                  "section-table-header-right-value"
                                )}
                              >
                                ${calculateSectionTotal(section).toFixed(2)}
                              </p>
                            </div>
                          )}

                          <PrimarySelectInput
                            name="section-type"
                            placeholder="Select Section Type"
                            value={
                              sectionType.find((st) => st.id === section.id)
                                ?.value || "SHOW_LINE_ITEMS"
                            }
                            onChange={(value) =>
                              handleSectionTypeChange(section.id, value)
                            }
                            options={[
                              {
                                label: "Show Line Items",
                                value: "SHOW_LINE_ITEMS",
                              },
                              {
                                label: "Hide Line Item Amounts",
                                value: "HIDE_LINE_ITEM_AMOUNTS",
                              },
                              {
                                label: "Hide Line Items",
                                value: "HIDE_LINE_ITEMS",
                              },
                            ]}
                            className={cx("select-input")}
                          />

                          <div className={cx("action-icon-container")}>
                            <div
                              onClick={(e) => handleActionClick(e, section)}
                              className={cx("action-icon-button")}
                            >
                              <ActionIcon className={cx("action-icon")} />
                            </div>
                            {activeMenu === section.id && (
                              <ActionMenu
                                section={section}
                                onClose={() => setActiveMenu(null)}
                                position={menuPosition}
                              />
                            )}
                          </div>

                          <PrimaryButton
                            icon={
                              <ArrowLeft2Icon
                                className={cx("arrow-down-icon", {
                                  rotated: collapsedSections[section.id],
                                })}
                              />
                            }
                            onClick={() => {
                              setCollapsedSections((prev) => ({
                                ...prev,
                                [section.id]: !prev[section.id],
                              }));
                            }}
                            className={cx("arrow-down-button")}
                          />
                        </div>
                      </div>
                      {!collapsedSections[section.id] && productCatalogError && (
                        <p className="text-sm text-yellow-500 mb-2">{productCatalogError}</p>
                      )}
                      {!collapsedSections[section.id] && (
                        <SectionTable
                          showTable={!collapsedSections[section.id]}
                          onDataChange={(data) =>
                            handleSectionTableDataChange(section.id, data)
                          }
                          listProductInvoice={listProductInvoice}
                          initialLines={
                            sectionTableData[section.id]
                              ? sectionTableData[section.id].formData
                              : section.lines
                          }
                        />
                      )}
                    </div>
                  </div>
                ))}

                <div className={cx("section-bottom")}>
                  <div className={cx("section-bottom-left")}></div>
                  <div className={cx("section-bottom-right")}>
                    <PrimaryButton
                      title="Add Section"
                      icon={<PlusIcon className={cx("plus-icon")} />}
                      onClick={handleAddSection}
                      className={cx("add-section-button")}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className={cx("section")}>
              <div className={cx("section-header")}>
                <h3 className={cx("section-title")}>Notes</h3>
              </div>

              <div className={cx("section-content")}>
                <div className={cx("options-container")}>
                  <div className={cx("option-item")}>
                    <div className={cx("option-item-content")}>
                      <div className={cx("option-item-content-item")}>
                        <label className={cx("option-item-content-item-label")}>
                          Customer Notes
                        </label>

                        <textarea
                          className={cx("textarea", "h-[171px]")}
                          placeholder="Enter notes"
                          name="customerNotes"
                          value={formData.customerNotes}
                          onChange={handleInputChange}
                          rows={4}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className={cx("options-container")}>
                  <div className={cx("option-item")}>
                    <div className={cx("option-item-content")}>
                      <div className={cx("option-item-content-item")}>
                        <label className={cx("option-item-content-item-label")}>
                          Internal Notes
                        </label>

                        <textarea
                          className={cx("textarea", "h-[171px]")}
                          placeholder="Enter notes"
                          name="internalNotes"
                          value={formData.internalNotes}
                          onChange={handleInputChange}
                          rows={4}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className={cx("section-content")}>
                <div className={cx("options-container", "w-full")}>
                  <div className={cx("option-item", "w-full")}>
                    <div className={cx("option-item-content")}>
                      <div className={cx("option-item-content-item")}>
                        <PrimaryInput
                          name="secondaryBillingAddress"
                          placeholder="Enter second Billing address"
                          type="text"
                          value={formData.secondaryBillingAddress}
                          onChange={handleInputChange}
                          className={cx("input")}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <div className={cx("footer")}>
        <div className={cx("footer-left")}>
          <h3 className={cx("footer-left-title")}>Summary:</h3>

          <div className={cx("footer-left-content")}>
            <p className={cx("footer-left-content-label")}>Invoice Total</p>
            <p className={cx("footer-left-content-value")}>
              ${calculateInvoiceTotal().toFixed(2)}
            </p>
          </div>

          <div className={cx("footer-left-content")}>
            <p className={cx("footer-left-content-label")}>Total Profit</p>
            <p className={cx("footer-left-content-value")}>
              ${calculateTotalProfit().toFixed(2)}
            </p>
          </div>

          <div className={cx("footer-left-content")}>
            <p className={cx("footer-left-content-label")}>Total Margin</p>
            <p className={cx("footer-left-content-value")}>
              {calculateTotalMargin().toFixed(2)}%
            </p>
          </div>
        </div>

        <div className={cx("footer-right")}>
          <div className={cx("summary-button-wrapper")} ref={summaryButtonRef}>
            <OutlinedButton
              title="Show Full Summary"
              onClick={handleShowSummaryClick}
              className={cx("show-full-summary-button")}
            />
            {showSummaryDropdown && summaryDropdownPosition && (
              <div
                className={cx("summary-dropdown")}
                data-summary-dropdown
                style={{
                  top: `${summaryDropdownPosition.top}px`,
                  left: `${summaryDropdownPosition.left}px`,
                }}
              >
                <div className={cx("summary-dropdown-content")}>
                  <div className={cx("summary-dropdown-header")}>
                    <h4 className={cx("summary-dropdown-title")}>
                      New Invoice
                    </h4>
                  </div>

                  <div className={cx("summary-dropdown-body")}>
                    <div className={cx("summary-row")}>
                      <div className={cx("summary-label")}>Material Cost</div>
                      <div className={cx("summary-value")}>
                        ${calculateMaterialCost().toFixed(2)}
                      </div>
                    </div>

                    <div className={cx("summary-row")}>
                      <div className={cx("summary-label")}>Labor Cost</div>
                      <div className={cx("summary-value")}>
                        ${calculateLaborCost().toFixed(2)}
                      </div>
                    </div>

                    <div className={cx("summary-divider")} />

                    <div className={cx("summary-row")}>
                      <div className={cx("summary-label")}>Cost</div>
                      <div className={cx("summary-value")}>
                        ${calculateCost().toFixed(2)}
                      </div>
                    </div>

                    <div className={cx("summary-row")}>
                      <div className={cx("summary-label")}>Markup</div>
                      <div className={cx("summary-value")}>
                        {calculateMarkupPercentage().toFixed(2)}% $
                        {calculateMarkupAmount().toFixed(2)}
                      </div>
                    </div>

                    <div className={cx("summary-row")}>
                      <div className={cx("summary-label")}>Subtotal</div>
                      <div className={cx("summary-value")}>
                        ${calculateSubtotal().toFixed(2)}
                      </div>
                    </div>

                    <div className={cx("summary-row")}>
                      <div className={cx("summary-label")}>Tax</div>
                      <div className={cx("summary-value")}>
                        ${calculateTax().toFixed(2)}
                      </div>
                    </div>

                    <div className={cx("summary-divider")} />

                    <div className={cx("summary-row", "summary-total")}>
                      <div className={cx("summary-label")}>Total</div>
                      <div className={cx("summary-value")}>
                        ${calculateTotal().toFixed(2)}
                      </div>
                    </div>

                    <div className={cx("summary-surcharge")}>
                      <div className={cx("summary-surcharge-header")}>
                        <WarningIcon className={cx("summary-surcharge-icon")} />
                        <div className={cx("summary-surcharge-title")}>
                          Surcharge
                        </div>
                      </div>
                      <div className={cx("summary-surcharge-description")}>
                        The customer will be charged the surcharge which will be
                        calculated at checkout.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateInvoice;
